"use client";

import { WhatsAppIcon } from "@/app/(public)/_components/marketing/hero/app-window/modules/module-inbox-channels-callout";
import {
  CAREERS_CALL_ALT,
  CAREERS_CALL_TITLE,
  CAREERS_INBOX_ALT,
  CAREERS_INBOX_TITLE,
  CAREERS_MOBILE_ALT,
  CAREERS_MOBILE_TITLE,
  CAREERS_WHATSAPP_ALT,
  CAREERS_WHATSAPP_TITLE,
} from "@/lib/careers-seo";
import { MessageSquare } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { CAREERS_BLUR_LIGHT } from "./careers-image-blur";
import { useCareersLanguage } from "./careers-language-context";

const CONTENT = {
  de: {
    teamChatTitle: "Team-Chat",
    teamChatSub: "Neue Nachricht",
    whatsappTitle: "Nachricht erhalten",
    whatsappSub: "Jonas Weber · WhatsApp",
    callTitle: "Anruf läuft",
    callSub: "Frau Berger · 02:14",
  },
  en: {
    teamChatTitle: "Team Chat",
    teamChatSub: "New message",
    whatsappTitle: "Message received",
    whatsappSub: "Jonas Weber · WhatsApp",
    callTitle: "Call in progress",
    callSub: "Ms. Berger · 02:14",
  },
} as const;

/** Desktop inbox mockup with a nested phone screenshot and 3 floating notification badges. */
export function CareersProductMockup(): ReactElement {
  const tI18n = useTranslations("public.careersProductMockup");
  const { lang } = useCareersLanguage();
  const t = CONTENT[lang];

  return (
    <div className="careers-product-mockup" style={{ position: "relative" }}>
      <div className="careers-mockup-bleed">
        <div style={{ position: "relative" }}>
          <Image
            src="/marketing/careers/ki-software-hausverwaltung-inbox.png"
            alt={CAREERS_INBOX_ALT}
            title={CAREERS_INBOX_TITLE}
            width={1488}
            height={902}
            sizes="(min-width: 900px) 800px, 100vw"
            priority
            placeholder="blur"
            blurDataURL={CAREERS_BLUR_LIGHT}
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              filter: "drop-shadow(0 32px 48px rgba(10,15,25,0.20))",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "2%",
              bottom: "6%",
              width: "25%",
              zIndex: 3,
              filter: "drop-shadow(0 22px 40px rgba(10,15,25,0.28))",
            }}
          >
            <div
              style={{
                position: "relative",
                borderRadius: "11.5% / 5.6%",
                background: "#0b0e12",
                padding: "2.3%",
              }}
            >
              <div
                style={{
                  position: "relative",
                  borderRadius: "9.6% / 4.7%",
                  overflow: "hidden",
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    paddingTop: "13%",
                    background: "#ffffff",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "26%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "28%",
                      height: "58%",
                      background: "#0b0e12",
                      borderRadius: 999,
                    }}
                  />
                </div>
                <Image
                  src="/marketing/careers/hausverwaltung-automatisieren-mobile-app.png"
                  alt={CAREERS_MOBILE_ALT}
                  title={CAREERS_MOBILE_TITLE}
                  width={982}
                  height={2126}
                  sizes="(min-width: 900px) 200px, 25vw"
                  placeholder="blur"
                  blurDataURL={CAREERS_BLUR_LIGHT}
                  style={{ display: "block", width: "100%", height: "auto" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingBadge
        className="careers-floating-badge careers-floating-badge-chat"
        style={{ top: "9%", right: "-3%" }}
      >
        <span
          style={{
            flex: "none",
            width: 28,
            height: 28,
            borderRadius: 8,
            background: "color-mix(in srgb, #008070 14%, #ffffff)",
            color: "#008070",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MessageSquare size={14} strokeWidth={2} />
        </span>
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: 1.25,
            minWidth: 0,
          }}
        >
          <span
            className="careers-floating-badge-title"
            style={{ fontSize: 11.5, fontWeight: 600, color: "#0a0f19" }}
          >
            {t.teamChatTitle}
          </span>
          <span
            className="careers-floating-badge-sub"
            style={{ fontSize: 10.5, color: "#6b7480" }}
          >
            {t.teamChatSub}
          </span>
        </span>
      </FloatingBadge>

      <FloatingBadge
        className="careers-floating-badge careers-floating-badge-whatsapp"
        style={{ bottom: "2%", left: "20%" }}
      >
        <Image
          src="/marketing/careers/ki-agenten-hausverwaltung-whatsapp.png"
          alt={CAREERS_WHATSAPP_ALT}
          title={CAREERS_WHATSAPP_TITLE}
          width={30}
          height={30}
          sizes="30px"
          style={{
            flex: "none",
            borderRadius: 999,
            objectFit: "cover",
            display: "block",
          }}
        />
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: 1.25,
            minWidth: 0,
          }}
        >
          <span
            className="careers-floating-badge-title"
            style={{ fontSize: 11.5, fontWeight: 600, color: "#0a0f19" }}
          >
            {t.whatsappTitle}
          </span>
          <span
            className="careers-floating-badge-sub"
            style={{ fontSize: 10.5, color: "#6b7480" }}
          >
            {t.whatsappSub}
          </span>
        </span>
        {/* Inline SVG: an external icon CDN would send visitor IPs to a third party. */}
        <span
          role="img"
          aria-label={tI18n("whatsapp")}
          style={{ flex: "none", display: "block", color: "#25D366" }}
        >
          <WhatsAppIcon size={17} />
        </span>
      </FloatingBadge>

      <FloatingBadge
        className="careers-floating-badge careers-floating-badge-call"
        style={{ bottom: "27%", left: "8%" }}
      >
        <Image
          src="/marketing/careers/hausverwaltung-ki-agent-telefonat.png"
          alt={CAREERS_CALL_ALT}
          title={CAREERS_CALL_TITLE}
          width={30}
          height={30}
          sizes="30px"
          style={{
            flex: "none",
            borderRadius: 999,
            objectFit: "cover",
            display: "block",
          }}
        />
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: 1.25,
            minWidth: 0,
          }}
        >
          <span
            className="careers-floating-badge-title"
            style={{ fontSize: 11.5, fontWeight: 600, color: "#0a0f19" }}
          >
            {t.callTitle}
          </span>
          <span
            className="careers-floating-badge-sub"
            style={{ fontSize: 10.5, color: "#6b7480" }}
          >
            {t.callSub}
          </span>
        </span>
        <span
          style={{
            flex: "none",
            width: 23,
            height: 23,
            borderRadius: 999,
            background: "color-mix(in srgb, #008070 14%, #ffffff)",
            color: "#008070",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M6.6 2.5a1.6 1.6 0 0 1 1.6.9l1.5 3a1.6 1.6 0 0 1-.36 1.9l-1.2 1a12 12 0 0 0 4.6 4.6l1-1.2a1.6 1.6 0 0 1 1.9-.36l3 1.5a1.6 1.6 0 0 1 .86 1.6l-.3 2.2a1.9 1.9 0 0 1-2.1 1.6C10.1 18.6 5.4 13.9 4.5 7.4A1.9 1.9 0 0 1 6.1 5.3z" />
          </svg>
        </span>
      </FloatingBadge>
    </div>
  );
}

function FloatingBadge(props: {
  readonly children: ReactNode;
  readonly style: CSSProperties;
  readonly className?: string;
}): ReactElement {
  return (
    <div
      className={props.className}
      style={{
        position: "absolute",
        zIndex: 4,
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#ffffff",
        border: "1px solid rgba(10,15,25,0.06)",
        borderRadius: 12,
        padding: "7px 10px",
        boxShadow: "0 14px 28px -14px rgba(10,15,25,0.28)",
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
}
